import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Production-safe logger - silences logs in production
const isDev = process.env.NODE_ENV === 'development';

export const logger = {
  log: (...args) => isDev && console.log(...args),
  error: (...args) => isDev && console.error(...args),
  warn: (...args) => isDev && console.warn(...args),
  info: (...args) => isDev && console.info(...args),
};

// Eastern Time utilities (UTC-5:00, handles DST automatically)
const EASTERN_TZ = 'America/New_York';

/**
 * Get current date in Eastern Time
 * @returns {Date} Date object representing current time in Eastern timezone
 */
export function getEasternNow() {
  return new Date(new Date().toLocaleString('en-US', { timeZone: EASTERN_TZ }));
}

/**
 * Get today's date string in Eastern Time (YYYY-MM-DD format)
 * @returns {string} Date string in ISO format
 */
export function getEasternDateString() {
  const eastern = getEasternNow();
  return `${eastern.getFullYear()}-${String(eastern.getMonth() + 1).padStart(2, '0')}-${String(eastern.getDate()).padStart(2, '0')}`;
}

/**
 * Get time until midnight Eastern Time in milliseconds
 * @returns {number} Milliseconds until midnight Eastern
 */
export function getTimeUntilMidnightEastern() {
  const eastern = getEasternNow();
  const tomorrow = new Date(eastern);
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);
  return tomorrow - eastern;
}

/**
 * Format a date string for display using Eastern Time context
 * @param {string} dateString - ISO date string (YYYY-MM-DD)
 * @returns {string} Formatted date string
 */
export function formatDateEastern(dateString) {
  const date = new Date(dateString + 'T12:00:00'); // Noon to avoid timezone edge cases
  return date.toLocaleDateString('en-US', { 
    weekday: 'long', 
    month: 'long', 
    day: 'numeric',
    timeZone: EASTERN_TZ
  });
}

/**
 * Check if the current device is mobile (screen width < 1024px)
 * @returns {boolean} True if mobile device
 */
export function isMobileDevice() {
  // Jigsaw Blocks app - always use mobile tables regardless of viewport
  // This ensures consistent behavior across all devices
  return true;
}

/**
 * Build API URL with is_mobile query parameter
 * @param {string} baseUrl - The base API URL
 * @param {object} params - Additional query parameters
 * @returns {string} URL with is_mobile parameter appended
 */
export function buildApiUrl(baseUrl, params = {}) {
  const url = new URL(baseUrl, window.location.origin);
  
  // Add is_mobile parameter based on current device
  url.searchParams.set('is_mobile', isMobileDevice().toString());
  
  // Add any additional parameters
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      url.searchParams.set(key, value.toString());
    }
  });
  
  return url.pathname + url.search;
}
